import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Typography } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import {
  SUBSCRIPTION_PATH,
  useSubscription,
} from "../../contexts/SubscriptionContext";
import { PAYMENT_REMINDER_DAYS } from "../../services/subscriptionService";

const DISMISS_KEY = "subscription_banner_dismissed";

/**
 * A slim notice above the page. Nothing renews by itself, so it shows during
 * a trial, from 3 days before a paid period ends, and through the grace days
 * after it. Dismissing hides it for this browser session only; in the grace
 * days it cannot be dismissed, because the dashboard is about to lock.
 */
const SubscriptionBanner: React.FC<{ sx?: object }> = ({ sx }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription } = useSubscription();
  // Inside a venue/clinic the packages open as a normal page of the layout.
  const venueMatch = location.pathname.match(/^\/dashboard\/([^/]+)/);
  const packagesPath = venueMatch
    ? `/dashboard/${venueMatch[1]}/subscription`
    : SUBSCRIPTION_PATH;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1",
  );

  if (!subscription) return null;
  const { status, daysLeft } = subscription;
  const days = daysLeft ?? 0;
  const grace = status === "GRACE";
  const show =
    status === "TRIAL" ||
    grace ||
    (status === "EXPIRING" && days <= PAYMENT_REMINDER_DAYS);
  if (!show || (dismissed && !grace)) return null;

  const dayText = `${days} day${days === 1 ? "" : "s"}`;
  const urgent = status !== "TRIAL" || days <= 3;
  const message = grace
    ? `Your subscription has ended. The dashboard locks in ${dayText}.`
    : status === "TRIAL"
      ? `Your free trial ends in ${dayText}.`
      : `Your subscription ends in ${dayText}.`;
  const hint =
    status === "TRIAL"
      ? "Pay for a package with Whish to keep using the system."
      : "It does not renew by itself: make a payment with Whish to continue.";

  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 2,
        py: 1,
        borderRadius: 2,
        border: "1px solid",
        borderColor: grace ? "#F0C2BD" : urgent ? "#F2D49B" : "#BFD8F2",
        bgcolor: grace ? "#FDF3F2" : urgent ? "#FDF6E7" : "#EEF5FC",
        color: grace ? "#8A2219" : urgent ? "#6B4600" : "#174A7C",
        flexWrap: "wrap",
        ...sx,
      }}
    >
      {urgent ? (
        <WarningAmberIcon sx={{ fontSize: 20 }} aria-hidden />
      ) : (
        <ScheduleIcon sx={{ fontSize: 20 }} aria-hidden />
      )}
      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, flexGrow: 1 }}>
        {message}{" "}
        <Box component="span" sx={{ fontWeight: 400 }}>
          {hint}
        </Box>
      </Typography>
      <Button
        size="small"
        variant="contained"
        disableElevation
        onClick={() => navigate(packagesPath)}
        sx={{ textTransform: "none", fontWeight: 600, minHeight: 36 }}
      >
        {status === "TRIAL" ? "See packages" : "Pay now"}
      </Button>
      {!grace && (
        <IconButton
          size="small"
          aria-label="Dismiss for now"
          onClick={() => {
            sessionStorage.setItem(DISMISS_KEY, "1");
            setDismissed(true);
          }}
          sx={{ color: "inherit" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default SubscriptionBanner;
